import { useMemo, useState } from 'react';

import { Button } from '@components/index';
import { STORAGE_KEYS } from '@constants/storage';
import { useGetStatusQuery } from '@features/lead-management/queries/LeadManagementQuery';
import { getLocalStorageItem } from '@utils/storage';
import { StatusEntity } from 'types/lead-management';

import AgentReassignmentToggle from './AgentReassignmentToggle';
import DraggableStatusList from './DraggableStatusList';
import { LeadStatusItem } from './status-data';

import theme from '../../../../../themes/colors';

const LeadSquaredConfiguration = () => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);

  const {
    data: statusResponse,
    isLoading: isLoadingStatuses,
    refetch: refetchStatuses
  } = useGetStatusQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId }
  );

  const initialStatuses: LeadStatusItem[] | undefined = useMemo(() => {
    if (!statusResponse || !Array.isArray(statusResponse)) return undefined;
    const totalCount = statusResponse.length;
    return statusResponse.map((status: StatusEntity, index: number) => ({
      ...status,
      isLocked: index === 0 || index >= totalCount - 2
    }));
  }, [statusResponse]);

  const [statuses, setStatuses] = useState<LeadStatusItem[] | undefined>(
    undefined
  );

  const hasStatuses = !!(statuses || initialStatuses);

  const handleRefresh = () => {
    setStatuses(undefined);
    refetchStatuses();
  };

  const {
    StatusList,
    startAddingStatus,
    isAddingNew,
    isUpdating,
    isMaxStatusesReached
  } = DraggableStatusList({
    statuses: statuses || initialStatuses,
    onStatusesChange: setStatuses,
    onRefresh: handleRefresh
  });

  return (
    <div className="flex flex-col gap-4">
      <AgentReassignmentToggle />
      <div className="flex flex-col rounded-xl bg-white">
        <div className="flex items-center justify-between gap-1 border-b border-cofeeGreyBlue200 px-6 py-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1 text-lg font-semibold text-cofeeNeutral10">
              Manage lead status
            </div>
            <div className="text-sm text-cofeeNeutral7">
              Configure the statuses for your lead management pipeline. You can
              add, edit, or reorder intermediate statuses.
            </div>
          </div>
          <Button
            variant="tertiary"
            className="!w-fit !whitespace-nowrap"
            size="small"
            label="Add status"
            labelStyle="!text-base !font-medium"
            leftIconName="plus"
            leftIconStroke={theme.cofeePrimary6}
            leftIconStyle="!h-4 !w-4"
            handleClick={startAddingStatus}
            disabled={
              isAddingNew ||
              isUpdating ||
              isLoadingStatuses ||
              !hasStatuses ||
              isMaxStatusesReached
            }
          />
        </div>
        <div className="p-6">
          {isLoadingStatuses || !hasStatuses ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-grey">Loading statuses...</div>
            </div>
          ) : (
            StatusList
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadSquaredConfiguration;
